/*
 * PDP gallery auto-rotation.
 *
 * Every 5s advance to the next image. Guards:
 *   - Pause on hover / focus / touch
 *   - Pause when tab hidden
 *   - Stop forever after user manually navigates (thumbnail click,
 *     swipe, arrow, etc.) - they're focused on a specific image now
 *   - No-op if prefers-reduced-motion or <2 media items
 *   - Loops forever (until user interacts) - wraps from last → first
 *     and keeps going.
 */
(function () {
  /* 8000ms cadence (was 5000ms) - slower PDP gallery auto-advance
     gives each angle room to be appreciated before sliding on.
     Reads as a deliberate showcase rather than a rapid carousel.
     Matches the slower premium cadence applied to the product
     card hover autoplay (3s) and the badge shine cycle (4.5s). */
  var INTERVAL_MS = 8000;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function start(gallery) {
    if (gallery.__autoplayBound) return;
    gallery.__autoplayBound = true;

    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    /* `[data-media-id]` matches both the real slide items AND the
       modal-opener clones nested inside each slide (used by the
       lightbox). Filter to the slider's direct visible children so
       indexOfActive/wrap logic uses the correct count. */
    function liveItems() {
      var list = viewer.querySelector('.product__media-list') || viewer;
      /* All real slides - NOT filtered by offsetWidth. On DESKTOP only the
         active slide is rendered (others display:none, swapped via
         thumbnails), so an offsetWidth>0 filter returned 1 item and
         autoplay never started (`items.length < 2`). The wrap clone is
         excluded by its class (it carries no data-media-id anyway). */
      return Array.from(list.children).filter(function (el) {
        return el.hasAttribute('data-media-id') && !el.classList.contains('autoplay-clone');
      });
    }
    var items = liveItems();
    if (items.length < 2) return;

    var paused = false;
    var stopped = false;
    var timer = null;
    /* In-flight lock - prevents autoplay tick from racing with a manual
       swipe wrap, and concurrent swipe wraps from stacking on top of
       each other. Set true at the moment a wrap starts; cleared in the
       wrap's settle callback. While true, advance() and touch handlers
       are no-ops. */
    var isWrapping = false;
    /* Timestamp until which scrolls are OUR OWN (autoplay advance / wrap
       tween). The user-scroll listener ignores scroll events inside this
       window so autoplay doesn't treat its own movement as a manual
       interaction and pause itself. */
    var programmaticUntil = 0;

    function indexOfActive() {
      var live = liveItems();
      for (var i = 0; i < live.length; i++) {
        if (live[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    /* Which slide is actually centered in the viewport RIGHT NOW. This is
       the source of truth for "where we are" - NOT the is-active class.
       media-gallery.js only updates is-active on setActiveMedia (thumbnail
       / autoplay), never on a native scroll-snap swipe. So after the user
       manually swipes, is-active is stale; basing the next advance on it
       makes autoplay jump from the wrong slide (e.g. user swipes back to A
       but is-active still says B, so autoplay goes B→C → looks like A→C).
       Measuring from scroll geometry stays correct through manual scroll
       and RTL. */
    function currentVisibleIndex() {
      var list = viewer.querySelector('.product__media-list') || viewer;
      var live = liveItems();
      if (!live.length) return 0;
      var listRect = list.getBoundingClientRect();
      var center = listRect.left + listRect.width / 2;
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < live.length; i++) {
        var r = live[i].getBoundingClientRect();
        var d = Math.abs((r.left + r.width / 2) - center);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    /* "Where we are" - switches by layout:
       • MOBILE horizontal slider → geometry (the centered slide), because
         native scroll moves the slide without updating is-active.
       • DESKTOP (one image shown, others display:none, swapped via
         setActiveMedia) → the is-active class, since there's no native
         scroll and the hidden slides have no useful geometry. */
    function currentIndex() {
      var list = viewer.querySelector('.product__media-list') || viewer;
      if (list.scrollWidth > list.clientWidth + 4) return currentVisibleIndex();
      return indexOfActive();
    }

    /* Wrap helper - clones the target slide, scrolls forward (or
       backward) into the clone, then snaps to the real target. Used
       by both autoplay (last → first) and manual swipe (past either
       end). Direction: 'forward' = clone first, append at end, scroll
       right; 'backward' = clone last, prepend at start, scroll left. */
    function wrapTo(targetItem, direction) {
      if (isWrapping) return;
      var list = targetItem.parentElement;
      if (!list || typeof list.scrollTo !== 'function') {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(targetItem.getAttribute('data-media-id'), false);
        }
        return;
      }
      if (list.scrollWidth <= list.clientWidth + 4) {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(targetItem.getAttribute('data-media-id'), false);
        }
        return;
      }

      // Acquire the in-flight lock. Pause autoplay too, so even if this
      // wrap was kicked off by the autoplay tick and the next tick fires
      // mid-animation, advance() bails out.
      isWrapping = true;
      var wrappingPausedAutoplay = !paused;
      paused = true;
      // The wrap tween scrolls programmatically - don't let the user-scroll
      // listener treat it as a manual interaction.
      programmaticUntil = Date.now() + 1500;

      /* Disable scroll-snap for the duration of the wrap. With
         `scroll-snap-type: x mandatory` the browser fights our programmatic
         smooth-scroll-to-clone - scrollLeft jitters against the snap points
         for ~800ms instead of settling, which is the C→A lag. Turning snap
         off lets the scroll complete cleanly; we restore it after snapping
         to the real target (which is itself snap-aligned). */
      var prevSnapType = list.style.scrollSnapType;
      var prevBehavior = list.style.scrollBehavior;
      list.style.scrollSnapType = 'none';
      list.style.scrollBehavior = 'auto';   // our rAF tween sets scrollLeft directly

      var clone = targetItem.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('autoplay-clone');

      var targetScrollLeft;       // where the clone sits (tween goal)
      if (direction === 'forward') {
        list.appendChild(clone);
        targetScrollLeft = clone.offsetLeft;
      } else {
        // Backward wrap: prepend clone of last item, instantly compensate
        // scroll so the visible slide doesn't jump, then tween to offset 0.
        list.insertBefore(clone, list.firstChild);
        list.scrollLeft = list.scrollLeft + clone.offsetWidth;
        targetScrollLeft = 0;
      }

      var finished = false;
      function finish() {
        if (finished) return;   // idempotent - rAF tween AND the safety timer both call it
        finished = true;
        var live = liveItems();
        live.forEach(function (el) { el.classList.remove('is-active'); });
        targetItem.classList.add('is-active');
        // Snap to the REAL target - identical image to the clone, so the
        // scrollLeft reset is invisible - then drop the clone.
        list.scrollLeft = targetItem.offsetLeft;
        clone.remove();
        var targetId = targetItem.getAttribute('data-media-id');
        if (gallery.elements && gallery.elements.thumbnails) {
          var thumbsRoot = gallery.elements.thumbnails;
          thumbsRoot.querySelectorAll('button').forEach(function (b) { b.removeAttribute('aria-current'); });
          var th = thumbsRoot.querySelector('[data-target="' + targetId + '"] button');
          if (th) th.setAttribute('aria-current', 'true');
        }
        requestAnimationFrame(function () {
          list.style.scrollBehavior = prevBehavior || '';
          list.style.scrollSnapType = prevSnapType || '';
          isWrapping = false;
          if (wrappingPausedAutoplay) paused = false;
        });
      }

      /* Controlled ~260ms ease-out tween to the clone, then snap to the
         real target. The browser's native `behavior:'smooth'` ran ~600ms
         here AND fought scroll-snap (jitter) - that was the C→A lag. A
         hand-rolled rAF tween (snap + scroll-behavior disabled above) is
         deterministic and matches the snappiness of a normal swipe. */
      var fromLeft = list.scrollLeft;
      var startTs = null;
      var DUR = 260;
      function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
      function step(ts) {
        if (finished) return;
        if (startTs === null) startTs = ts;
        var p = Math.min(1, (ts - startTs) / DUR);
        list.scrollLeft = fromLeft + (targetScrollLeft - fromLeft) * easeOutCubic(p);
        if (p < 1) requestAnimationFrame(step);
        else finish();
      }
      requestAnimationFrame(step);
      /* Safety net: if rAF is interrupted (tab hidden mid-wrap, etc.) the
         tween could stall and leave isWrapping stuck → gallery frozen.
         Guarantee finish() runs. */
      setTimeout(finish, DUR + 300);
    }

    /* DESKTOP slide. The desktop gallery shows ONE image and swaps via a
       display:none↔flex toggle (setActiveMedia) - a hard cut that looks
       jerky on autoplay. To get a smooth carousel feel we build a transient
       2-image track over the gallery [next | current], start it showing the
       CURRENT image, then translate it so the NEXT image slides in from the
       LEFT (both images move together - a real push, not a dissolve). When
       the slide finishes we flip the real active image underneath and drop
       the overlay, so it's seamless. We clone only the <img>s (not the whole
       items) to avoid duplicating the Lottie "NEW" badge / FOMO overlays.
       Mobile keeps its native scroll-tween slider and isn't touched. */
    var SLIDE_MS = 480;
    function buildCell(srcImg) {
      var c = document.createElement('div');
      c.style.cssText = 'flex:0 0 50%;width:50%;height:100%;';
      var im = srcImg.cloneNode(true);
      im.removeAttribute('id');
      im.removeAttribute('loading');
      im.style.width = '100%';
      im.style.height = '100%';
      im.style.objectFit = getComputedStyle(srcImg).objectFit || 'contain';
      c.appendChild(im);
      return c;
    }
    function slideTo(mediaId) {
      if (typeof gallery.setActiveMedia !== 'function') return;
      var list = viewer.querySelector('.product__media-list') || viewer;
      var current = list.querySelector('.product__media-item.is-active');
      var target = list.querySelector('[data-media-id="' + mediaId + '"]');
      var oldImg = current && current.querySelector('img');
      var newImg = target && target.querySelector('img');

      // Can't build the slide → just swap.
      if (!current || !oldImg || !newImg) { gallery.setActiveMedia(mediaId, false); return; }

      var stale = list.querySelector('.autoplay-slide');
      if (stale) stale.remove();                 // interrupted previous slide

      var listRect = list.getBoundingClientRect();
      // Geometry MUST come from the real <img>, not the item. The item box is
      // larger than the image (it carries padding, e.g. 0 0 10px 10px), so
      // sizing the overlay to the item rendered the clone ~2–3% bigger than
      // the real image - it looked like the image grew during the slide and
      // snapped back to normal when the overlay was removed. Matching the
      // img's own box keeps the clone pixel-identical to the live image.
      var imgRect = oldImg.getBoundingClientRect();

      // Opaque box (background matches the white product canvas) so the real
      // image underneath never peeks through the moving cells.
      // z-index MUST stay below the pagination dots (z-index 4) but above the
      // media (z-index auto). The media list is NOT a stacking context, so a
      // higher z-index here would paint the overlay OVER the dots and hide
      // them for the slide's duration. z-index 3 covers the image yet leaves
      // the dots - with their original glass look - fully on top.
      var box = document.createElement('div');
      box.className = 'autoplay-slide';
      box.setAttribute('aria-hidden', 'true');
      box.style.cssText =
        'position:absolute;pointer-events:none;z-index:3;overflow:hidden;background:#fff;' +
        'left:' + (imgRect.left - listRect.left) + 'px;' +
        'top:' + (imgRect.top - listRect.top) + 'px;' +
        'width:' + imgRect.width + 'px;height:' + imgRect.height + 'px;';

      // Direction is locale-aware. In LTR the user expects "next" to come
      // from the RIGHT (left→right reading). In RTL ("ar") that feels
      // backwards - the user expects the new image to enter from the LEFT.
      // We detect from the document direction so both desktop locales get
      // a natural push.
      var dirRTL = (document.documentElement.dir === 'rtl') ||
                   (getComputedStyle(list).direction === 'rtl');

      var track = document.createElement('div');
      // 200% wide = two image-width cells.
      // LTR: [current | next], start translateX(0), animate to -50%
      //      → track moves LEFT, NEXT enters from the RIGHT.
      // RTL: [next | current], start translateX(-50%), animate to 0
      //      → track moves RIGHT, NEXT enters from the LEFT.
      // NOTE: deliberately NO `will-change: transform`. It promotes the track
      // to its own composited layer, which the pagination dots' backdrop-filter
      // (blur) can't sample over - making the dots flicker out during the
      // slide. Plain transform animates fine without it here.
      var startTx = dirRTL ? 'translateX(-50%)' : 'translateX(0)';
      // Force LTR on the track: its parent inherits `direction:rtl` from
      // the slider, which makes our flex children flow right-to-left -
      // so `appendChild(left)` then `appendChild(right)` actually lays
      // them out as [right | left] visually, and the translateX tween
      // shows the wrong cell first → image looks like it jumps/lags
      // through the wrong frame on every advance. Pinning the track to
      // LTR keeps appendChild order = visual left-to-right in both
      // locales; the transform math is direction-agnostic anyway.
      track.style.cssText =
        'position:absolute;top:0;left:0;height:100%;width:200%;display:flex;' +
        'direction:ltr;' +
        'transform:' + startTx + ';';
      if (dirRTL) {
        track.appendChild(buildCell(newImg));    // left cell  = next
        track.appendChild(buildCell(oldImg));    // right cell = current
      } else {
        track.appendChild(buildCell(oldImg));    // left cell  = current
        track.appendChild(buildCell(newImg));    // right cell = next
      }
      box.appendChild(track);

      var changedPos = false;
      if (getComputedStyle(list).position === 'static') {
        list.style.position = 'relative';
        changedPos = true;
      }
      list.appendChild(box);

      // Swap the REAL image underneath NOW, while it's fully hidden by the
      // opaque overlay. When the overlay is removed the new image is already
      // in place, so there's no lingering-old-frame at the end.
      gallery.setActiveMedia(mediaId, false);

      // Two rAFs so the start transform paints before the transition begins.
      // LTR ends at -50% (moved left), RTL ends at 0 (moved right).
      var endTx = dirRTL ? 'translateX(0)' : 'translateX(-50%)';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          track.style.transition = 'transform ' + SLIDE_MS + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)';
          track.style.transform = endTx;
        });
      });

      // Slide finished → drop the overlay, revealing the (already-set) new image.
      setTimeout(function () {
        box.remove();
        if (changedPos && !list.querySelector('.autoplay-slide')) list.style.position = '';
      }, SLIDE_MS + 40);
    }

    function advance() {
      if (stopped) return;
      /* Self-clean zombie timers. On an AJAX option swap the old
         <media-gallery> is detached and replaced, but its setInterval is
         only cleared on user interaction - so the detached gallery's timer
         keeps ticking while linkedOptionsLoaded starts a fresh timer on the
         new gallery. Two loops race and the image auto-scroll jumps. If our
         gallery is no longer in the document, stop this timer for good. */
      if (!gallery.isConnected) { stop(); return; }
      if (paused || document.hidden) return;
      if (isWrapping) return;
      var live = liveItems();
      if (live.length < 2) return;
      // Mark the upcoming scroll as ours so the user-scroll listener
      // doesn't mistake it for a manual interaction.
      programmaticUntil = Date.now() + 1500;
      // Advance from where we actually are (geometry on mobile slider,
      // is-active on desktop swap) - see currentIndex().
      var idx = currentIndex();
      var list = viewer.querySelector('.product__media-list') || viewer;
      var isSlider = list.scrollWidth > list.clientWidth + 4;
      var nextIdx = idx === live.length - 1 ? 0 : idx + 1;

      if (!isSlider) {
        /* DESKTOP: smooth right→left push slide (next image enters from the
           right). Also handles the last → first wrap, since there's nothing
           to scroll. */
        slideTo(live[nextIdx].getAttribute('data-media-id'));
      } else if (idx === live.length - 1) {
        /* MOBILE infinite loop: last → first goes FORWARD (a clone of the
           first slides in from the right), not a scroll-back. */
        wrapTo(live[0], 'forward');
      } else if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(live[idx + 1].getAttribute('data-media-id'), false);
      }
    }

    /* Manual touch wrap: when the user is at the right (or left) edge
       of the slider and swipes past it, trigger the wrap. Native
       overflow scroll just stops at the boundary, so we have to add
       this behavior ourselves. */
    var slider = viewer.querySelector('.product__media-list') || viewer;
    var isRTL = slider ? getComputedStyle(slider).direction === 'rtl' : false;
    if (slider && typeof slider.addEventListener === 'function') {
      var swipeStartX = 0;
      var swipeStartY = 0;
      var startedAtFirst = false;
      var startedAtLast = false;
      var trackingTouch = false;

      /* Manual-scroll guard. When the user scrolls/swipes the gallery,
         pause autoplay and DON'T resume until they've been idle ~1.6s -
         then restart a fresh full-interval countdown. Without this,
         autoplay's next tick yanked the image away right after the user
         settled on a slide. Covers every input (touch swipe, wheel,
         trackpad, scrollbar) since they all emit `scroll`. Our own
         autoplay/wrap scrolls are skipped via programmaticUntil. */
      var idleResumeTimer = null;
      slider.addEventListener('scroll', function () {
        if (Date.now() < programmaticUntil) return; // our own scroll
        paused = true;
        clearTimeout(idleResumeTimer);
        idleResumeTimer = setTimeout(function () {
          paused = false;
          schedule(); // fresh countdown so the next advance is a full interval away
        }, 1600);
      }, { passive: true });

      // Attach to document at CAPTURE phase so native scroll handling
      // can't preempt - and so we receive events even if the touch
      // target is a nested .media a / <img>.
      function inOurSlider(target) {
        return !!(target && target.closest && target.closest('.product__media-list') === slider);
      }

      document.addEventListener('touchstart', function (e) {
        if (!e.touches || !e.touches.length) return;
        if (!inOurSlider(e.target)) return;
        if (isWrapping) return; // ignore taps that arrive mid-wrap
        var t = e.touches[0];
        swipeStartX = t.clientX;
        swipeStartY = t.clientY;
        trackingTouch = true;
        var live = liveItems();
        var idx = currentVisibleIndex();
        startedAtFirst = idx === 0;
        startedAtLast = idx === live.length - 1;
      }, { capture: true, passive: true });

      // touchcancel covers iOS interruptions (multi-touch, system gesture)
      // - clear the flag so a stale trackingTouch doesn't trigger a
      // bogus wrap on the next touchend.
      document.addEventListener('touchcancel', function () {
        trackingTouch = false;
      }, { capture: true, passive: true });

      document.addEventListener('touchend', function (e) {
        if (!trackingTouch) return;
        trackingTouch = false;
        if (!inOurSlider(e.target)) return;
        if (isWrapping) return; // a wrap already in flight - drop this swipe
        var t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        var dx = t.clientX - swipeStartX;
        var dy = t.clientY - swipeStartY;
        if (Math.abs(dx) < 40) return;
        // Ignore mostly-vertical swipes (page scroll, not slider gesture)
        if (Math.abs(dy) > Math.abs(dx)) return;

        var live = liveItems();
        if (live.length < 2) return;

        // Map dx into intent. LTR: finger-left (dx<0) = next.
        // RTL flips the mapping.
        var wantsNext = isRTL ? dx > 40 : dx < -40;
        var wantsPrev = isRTL ? dx < -40 : dx > 40;

        /* Wrap past either edge - forward (last→first) or backward
           (first→last) - for a true infinite loop. */
        if (wantsNext && startedAtLast) {
          wrapTo(live[0], 'forward');
        } else if (wantsPrev && startedAtFirst) {
          wrapTo(live[live.length - 1], 'backward');
        }
      }, { capture: true, passive: true });
    }

    function schedule() {
      clearInterval(timer);
      timer = setInterval(advance, INTERVAL_MS);
    }

    function stop() {
      stopped = true;
      clearInterval(timer);
      timer = null;
    }

    // Pause on hover/focus/touch
    ['mouseenter', 'focusin', 'touchstart'].forEach(function (ev) {
      gallery.addEventListener(ev, function () { paused = true; }, { passive: true });
    });
    ['mouseleave', 'focusout', 'touchend', 'touchcancel'].forEach(function (ev) {
      gallery.addEventListener(ev, function () { paused = false; }, { passive: true });
    });

    // User-initiated navigation → stop autoplay for the session.
    // Thumbnail clicks, prev/next buttons, swipe - any click inside
    // the gallery that isn't the auto-advance itself.
    var userInteracted = false;
    gallery.addEventListener('click', function () {
      if (userInteracted) return;
      userInteracted = true;
      stop();
    }, { passive: true });
    gallery.addEventListener('keydown', function (e) {
      // Arrow keys / Enter / Space on focused controls
      if (['ArrowLeft', 'ArrowRight', 'Enter', ' '].indexOf(e.key) === -1) return;
      if (userInteracted) return;
      userInteracted = true;
      stop();
    });

    document.addEventListener('visibilitychange', function () {
      // visibilitychange handles pause/resume by checking document.hidden
      // in advance(); nothing else to do.
    });

    schedule();
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind on AJAX section swap (linked-options-ajax.js)
  document.addEventListener('linkedOptionsLoaded', function () {
    setTimeout(init, 100);
  });
})();
